import { TableHead } from '@talentmesh/ui/components/table';
import { cn } from '@talentmesh/ui/lib/utils';

import { type CSSProperties } from 'react';

import { useDirection } from '../../lib/hooks';
import { useMRTCompatibleTheme } from '../../lib/mrt-theme';
import { type MRT_Header, type MRT_RowData, type MRT_TableInstance } from '../../types';
import { type TableThProps } from '../../types/mrt-ui-props';
import { mergeCssVars, resolveThemeStyle } from '../../utils/mrt-style';
import { parseCSSVarId } from '../../utils/style.utils';
import { parseFromValuesOrFunc } from '../../utils/utils';
import classes from './MRT_TableFooterCell.module.css';

interface Props<TData extends MRT_RowData> extends TableThProps {
    footer: MRT_Header<TData>;
    renderedColumnIndex?: number;
    table: MRT_TableInstance<TData>;
}

export const MRT_TableFooterCell = <TData extends MRT_RowData>({
    footer,
    renderedColumnIndex,
    table,
    ...rest
}: Props<TData>) => {
    const { dir } = useDirection();
    const theme = useMRTCompatibleTheme();
    const {
        options: { enableColumnPinning, layoutMode, mantineTableFooterCellProps },
    } = table;
    const { column } = footer;
    const { columnDef } = column;
    const { columnDefType } = columnDef;

    const isColumnPinned = enableColumnPinning && columnDef.columnDefType !== 'group' && column.getIsPinned();

    const args = { column, table };
    const tableCellProps = {
        ...parseFromValuesOrFunc(mantineTableFooterCellProps, args),
        ...parseFromValuesOrFunc(columnDef.mantineTableFooterCellProps, args),
        ...rest,
    };

    const widthStyles: CSSProperties = {
        minWidth: `max(calc(var(--header-${parseCSSVarId(footer?.id)}-size) * 1px), ${columnDef.minSize ?? 30}px)`,
        width: `calc(var(--header-${parseCSSVarId(footer.id)}-size) * 1px)`,
    };
    if (layoutMode === 'grid') {
        widthStyles.flex = `${
            [0, false].includes(columnDef.grow!) ? 0 : `var(--header-${parseCSSVarId(footer.id)}-size)`
        } 0 auto`;
    } else if (layoutMode === 'grid-no-grow') {
        widthStyles.flex = `${+(columnDef.grow || 0)} 0 auto`;
    }

    const rawAlign = (tableCellProps as { align?: string }).align;
    const textAlign: 'center' | 'left' | 'right' =
        rawAlign === 'center' || rawAlign === 'right' || rawAlign === 'left'
            ? rawAlign
            : columnDefType === 'group'
              ? 'center'
              : dir === 'rtl'
                ? 'right'
                : 'left';

    const cssVars = mergeCssVars({
        '--mrt-cell-align': textAlign,
        '--mrt-table-cell-start': isColumnPinned === 'start' ? `${column.getStart(isColumnPinned)}` : undefined,
        '--mrt-table-cell-end': isColumnPinned === 'end' ? `${column.getAfter(isColumnPinned)}` : undefined,
        ...(tableCellProps as { __vars?: Record<string, number | string | undefined> }).__vars,
    });

    const resolvedStyle = resolveThemeStyle(
        tableCellProps.style as ((t: typeof theme) => CSSProperties) | CSSProperties | undefined,
        theme,
    );

    const {
        __vars: _v,
        align: _a,
        children: cellChildren,
        className: cellClassName,
        style: _s,
        ...restCellProps
    } = tableCellProps as {
        __vars?: Record<string, number | string | undefined>;
    } & TableThProps;

    return (
        <TableHead
            colSpan={footer.colSpan}
            data-column-pinned={isColumnPinned || undefined}
            data-first-end-pinned={(isColumnPinned === 'end' && column.getIsFirstColumn(isColumnPinned)) || undefined}
            data-index={renderedColumnIndex}
            data-last-start-pinned={(isColumnPinned === 'start' && column.getIsLastColumn(isColumnPinned)) || undefined}
            {...restCellProps}
            className={cn(
                classes.root,
                layoutMode?.startsWith('grid') && classes.grid,
                columnDefType === 'group' && classes.group,
                cellClassName,
            )}
            style={{
                ...cssVars,
                ...widthStyles,
                ...resolvedStyle,
                textAlign,
            }}
        >
            {cellChildren ??
                (footer.isPlaceholder
                    ? null
                    : (parseFromValuesOrFunc(columnDef.Footer, {
                          column,
                          footer,
                          table,
                      }) ??
                      columnDef.footer ??
                      null))}
        </TableHead>
    );
};
